interface LoadingProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Full screen mode (centered in viewport) */
  fullScreen?: boolean;
  /** Show loading text */
  text?: string;
  /** Custom className for container */
  className?: string;
}

const sizeClasses = {
  sm: 'h-6 w-6 border-2',
  md: 'h-10 w-10 border-3',
  lg: 'h-12 w-12 border-4',
};

/**
 * Loading spinner component with multiple variants
 */
export function Loading({
  size = 'lg',
  fullScreen = true,
  text,
  className = '',
}: LoadingProps) {
  const spinner = (
    <div
      className={`animate-spin rounded-full border-tg-button border-t-transparent ${sizeClasses[size]}`}
    />
  );

  if (fullScreen) {
    return (
      <div className={`flex flex-col items-center justify-center min-h-screen gap-3 ${className}`}>
        {spinner}
        {text && <p className="text-tg-hint text-sm animate-pulse">{text}</p>}
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center gap-2 py-8 ${className}`}>
      {spinner}
      {text && <p className="text-tg-hint text-sm">{text}</p>}
    </div>
  );
}

/**
 * Inline loading indicator for buttons or small areas
 */
export function LoadingInline({ className = '' }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center gap-1 ${className}`}>
      <div className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <div className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <div className="w-1.5 h-1.5 bg-current rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

/**
 * Loading overlay for covering content while loading
 */
export function LoadingOverlay({ text }: { text?: string }) {
  return (
    <div className="absolute inset-0 bg-tg-bg/80 backdrop-blur-sm flex flex-col items-center justify-center z-50 gap-3">
      <div className="animate-spin rounded-full h-10 w-10 border-3 border-tg-button border-t-transparent" />
      {text && <p className="text-tg-hint text-sm">{text}</p>}
    </div>
  );
}

